import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Search, Trash2 } from 'lucide-react';
import type { CategoryToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { serverMessage } from '~/lib/api/server-error';
import { useManufacturersList } from '~/hooks/use-manufacturers';
import {
	useCategoryProducts,
	useCategoryProductMutations,
	useCategoryManufacturers,
	useCategoryManufacturerMutations,
	useCategoryNews,
	useCategoryNewsMutations,
	useCategoryAttributes,
	useCategoryAttributeMutations
} from '~/hooks/use-category-relations';

export type RelationKind = 'products' | 'manufacturers' | 'news' | 'attributes';

/** Soft cap on client-rendered rows (these endpoints return the full related list — products can be huge). */
const ROW_CAP = 500;

/* ------------------------------------------------------------------ shared shell + table bits */

function RelationShell({
	open,
	onClose,
	title,
	subtitle,
	children
}: {
	open: boolean;
	onClose: () => void;
	title: string;
	subtitle: string;
	children: React.ReactNode;
}) {
	return (
		<Dialog open={open} onOpenChange={o => !o && onClose()}>
			<DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-3xl">
				<DialogHeader className="border-b border-border p-5">
					<DialogTitle>{title}</DialogTitle>
					<DialogDescription>{subtitle}</DialogDescription>
				</DialogHeader>
				{children}
			</DialogContent>
		</Dialog>
	);
}

function CountNote({ shown, total }: { shown: number; total: number }) {
	const { t } = useTranslation();
	if (total <= ROW_CAP) return <span>{t('catalog.categories.relations.count', { count: total })}</span>;
	return <span className="text-warning-fg">{t('catalog.categories.relations.capped', { shown, total })}</span>;
}

function ToggleCell({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
	return <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />;
}

/** Inline sort-index input that commits on blur / Enter. */
function SortCell({ value, onCommit }: { value: number; onCommit: (v: number) => void }) {
	const [local, setLocal] = useState(String(value ?? 0));
	return (
		<Input
			type="number"
			className="h-8 w-20"
			value={local}
			onChange={e => setLocal(e.target.value)}
			onBlur={() => local !== String(value) && onCommit(Number(local) || 0)}
			onKeyDown={e => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
		/>
	);
}

/* ------------------------------------------------------------------ Products (featured toggle) */

function ProductsManager({ category, onClose }: { category: CategoryToken; onClose: () => void }) {
	const { t } = useTranslation();
	const cid = category.id!;
	const { data, isLoading } = useCategoryProducts(cid);
	const { update } = useCategoryProductMutations(cid);
	const [q, setQ] = useState('');

	const rows = data ?? [];
	const filtered = useMemo(() => {
		const s = q.trim().toLowerCase();
		return s ? rows.filter(r => (r.nm ?? '').toLowerCase().includes(s) || (r.cd ?? '').toLowerCase().includes(s)) : rows;
	}, [rows, q]);
	const shown = filtered.slice(0, ROW_CAP);

	return (
		<RelationShell
			open
			onClose={onClose}
			title={t('catalog.categories.relations.productsTitle', { name: category.nm })}
			subtitle={t('catalog.categories.relations.productsSubtitle')}>
			<div className="flex items-center justify-between gap-3 border-b border-border p-3 text-xs text-foreground-muted">
				<div className="relative max-w-xs flex-1">
					<Search size={14} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-foreground-subtle" />
					<Input value={q} onChange={e => setQ(e.target.value)} placeholder={t('common.search')} className="h-8 pl-8" />
				</div>
				<CountNote shown={shown.length} total={filtered.length} />
			</div>
			<ScrollArea className="max-h-[55vh]">
				<table className="w-full text-sm">
					<thead className="sticky top-0 bg-elevated text-xs text-foreground-muted">
						<tr className="border-b border-border">
							<th className="px-4 py-2 text-left font-medium">{t('catalog.categories.relations.colName')}</th>
							<th className="w-32 px-4 py-2 text-left font-medium">{t('catalog.categories.relations.colCode')}</th>
							<th className="w-24 px-4 py-2 text-center font-medium">{t('catalog.categories.relations.colFeatured')}</th>
						</tr>
					</thead>
					<tbody>
						{isLoading ? (
							<tr>
								<td colSpan={3} className="px-4 py-8 text-center text-foreground-muted">
									…
								</td>
							</tr>
						) : (
							shown.map(p => (
								<tr key={p.id} className="border-b border-border/60 hover:bg-muted/40">
									<td className="px-4 py-2 text-foreground">{p.nm}</td>
									<td className="px-4 py-2 text-foreground-muted">{p.cd}</td>
									<td className="px-4 py-2 text-center">
										<ToggleCell
											checked={Boolean(p.isFeatured)}
											label={t('catalog.categories.relations.colFeatured')}
											onChange={v =>
												update.mutate({ id: p.id, isFeatured: v, si: p.si } as never, {
													onError: err => toast.error(serverMessage(err))
												})
											}
										/>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</ScrollArea>
		</RelationShell>
	);
}

/* ------------------------------------------------------------------ Manufacturers (add/toggle/sort/remove) */

function ManufacturersManager({ category, onClose }: { category: CategoryToken; onClose: () => void }) {
	const { t } = useTranslation();
	const cid = category.id!;
	const { data, isLoading } = useCategoryManufacturers(cid);
	const { update, add, remove } = useCategoryManufacturerMutations(cid);
	const { data: allManufacturers } = useManufacturersList();
	const [toAdd, setToAdd] = useState<string>('');

	const rows = data ?? [];
	const existingIds = new Set(rows.map(r => r.mfid));
	const addable = (allManufacturers ?? []).filter(m => m.id != null && !existingIds.has(m.id));

	const onErr = (err: unknown) => toast.error(serverMessage(err));

	return (
		<RelationShell
			open
			onClose={onClose}
			title={t('catalog.categories.relations.manufacturersTitle', { name: category.nm })}
			subtitle={t('catalog.categories.relations.manufacturersSubtitle')}>
			<div className="flex items-center gap-2 border-b border-border p-3">
				<Select value={toAdd} onValueChange={setToAdd}>
					<SelectTrigger className="h-9 w-72">
						<SelectValue placeholder={t('catalog.categories.relations.selectManufacturer')} />
					</SelectTrigger>
					<SelectContent>
						{addable.map(m => (
							<SelectItem key={m.id} value={String(m.id)}>
								{m.nm}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Button
					size="sm"
					disabled={!toAdd || add.isPending}
					onClick={() => add.mutate(Number(toAdd), { onSuccess: () => setToAdd(''), onError: onErr })}>
					<Plus size={14} /> {t('common.add')}
				</Button>
			</div>
			<ScrollArea className="max-h-[55vh]">
				<table className="w-full text-sm">
					<thead className="sticky top-0 bg-elevated text-xs text-foreground-muted">
						<tr className="border-b border-border">
							<th className="px-4 py-2 text-left font-medium">{t('catalog.categories.relations.colName')}</th>
							<th className="w-24 px-4 py-2 text-left font-medium">{t('catalog.categories.relations.colSort')}</th>
							<th className="w-24 px-4 py-2 text-center font-medium">{t('catalog.categories.relations.colFeatured')}</th>
							<th className="w-12 px-4 py-2" />
						</tr>
					</thead>
					<tbody>
						{isLoading ? (
							<tr>
								<td colSpan={4} className="px-4 py-8 text-center text-foreground-muted">
									…
								</td>
							</tr>
						) : rows.length === 0 ? (
							<tr>
								<td colSpan={4} className="px-4 py-8 text-center text-foreground-muted">
									{t('common.noResults')}
								</td>
							</tr>
						) : (
							rows.map(m => (
								<tr key={m.id} className="border-b border-border/60 hover:bg-muted/40">
									<td className="px-4 py-2 text-foreground">{m.mfnm}</td>
									<td className="px-4 py-2">
										<SortCell
											value={m.si ?? 0}
											onCommit={si => update.mutate({ id: m.id, si, isFeatured: m.isFeatured } as never, { onError: onErr })}
										/>
									</td>
									<td className="px-4 py-2 text-center">
										<ToggleCell
											checked={Boolean(m.isFeatured)}
											label={t('catalog.categories.relations.colFeatured')}
											onChange={v => update.mutate({ id: m.id, si: m.si, isFeatured: v } as never, { onError: onErr })}
										/>
									</td>
									<td className="px-4 py-2 text-center">
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-danger-fg"
											onClick={() => remove.mutate(m.id!, { onError: onErr })}
											aria-label={t('common.delete')}>
											<Trash2 size={14} />
										</Button>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</ScrollArea>
		</RelationShell>
	);
}

/* ------------------------------------------------------------------ News (toggle/sort/remove) */

function NewsManager({ category, onClose }: { category: CategoryToken; onClose: () => void }) {
	const { t } = useTranslation();
	const cid = category.id!;
	const { data, isLoading } = useCategoryNews(cid);
	const { update, remove } = useCategoryNewsMutations(cid);
	const onErr = (err: unknown) => toast.error(serverMessage(err));
	const rows = data ?? [];

	return (
		<RelationShell
			open
			onClose={onClose}
			title={t('catalog.categories.relations.newsTitle', { name: category.nm })}
			subtitle={t('catalog.categories.relations.newsSubtitle')}>
			<ScrollArea className="max-h-[60vh]">
				<table className="w-full text-sm">
					<thead className="sticky top-0 bg-elevated text-xs text-foreground-muted">
						<tr className="border-b border-border">
							<th className="px-4 py-2 text-left font-medium">{t('catalog.categories.relations.colTitle')}</th>
							<th className="w-24 px-4 py-2 text-left font-medium">{t('catalog.categories.relations.colSort')}</th>
							<th className="w-24 px-4 py-2 text-center font-medium">{t('catalog.categories.relations.colFeatured')}</th>
							<th className="w-12 px-4 py-2" />
						</tr>
					</thead>
					<tbody>
						{isLoading ? (
							<tr>
								<td colSpan={4} className="px-4 py-8 text-center text-foreground-muted">
									…
								</td>
							</tr>
						) : rows.length === 0 ? (
							<tr>
								<td colSpan={4} className="px-4 py-8 text-center text-foreground-muted">
									{t('common.noResults')}
								</td>
							</tr>
						) : (
							rows.map(n => (
								<tr key={n.id} className="border-b border-border/60 hover:bg-muted/40">
									<td className="px-4 py-2 text-foreground">{n.netl}</td>
									<td className="px-4 py-2">
										<SortCell
											value={n.si ?? 0}
											onCommit={si => update.mutate({ id: n.id, si, isFeatured: n.isFeatured } as never, { onError: onErr })}
										/>
									</td>
									<td className="px-4 py-2 text-center">
										<ToggleCell
											checked={Boolean(n.isFeatured)}
											label={t('catalog.categories.relations.colFeatured')}
											onChange={v => update.mutate({ id: n.id, si: n.si, isFeatured: v } as never, { onError: onErr })}
										/>
									</td>
									<td className="px-4 py-2 text-center">
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-danger-fg"
											onClick={() => remove.mutate(n.id!, { onError: onErr })}
											aria-label={t('common.delete')}>
											<Trash2 size={14} />
										</Button>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</ScrollArea>
			<p className="border-t border-border px-4 py-2 text-xs text-foreground-subtle">{t('catalog.categories.relations.newsAddDeferred')}</p>
		</RelationShell>
	);
}

/* ------------------------------------------------------------------ Attributes (hide + featured toggle) */

function AttributesManager({ category, onClose }: { category: CategoryToken; onClose: () => void }) {
	const { t } = useTranslation();
	const cid = category.id!;
	const { data, isLoading } = useCategoryAttributes(cid);
	const { update } = useCategoryAttributeMutations(cid);
	const onErr = (err: unknown) => toast.error(serverMessage(err));
	const rows = data ?? [];

	return (
		<RelationShell
			open
			onClose={onClose}
			title={t('catalog.categories.relations.attributesTitle', { name: category.nm })}
			subtitle={t('catalog.categories.relations.attributesSubtitle')}>
			<ScrollArea className="max-h-[60vh]">
				<table className="w-full text-sm">
					<thead className="sticky top-0 bg-elevated text-xs text-foreground-muted">
						<tr className="border-b border-border">
							<th className="px-4 py-2 text-left font-medium">{t('catalog.categories.relations.colName')}</th>
							<th className="w-32 px-4 py-2 text-left font-medium">{t('catalog.categories.relations.colCode')}</th>
							<th className="w-20 px-4 py-2 text-center font-medium">{t('catalog.categories.relations.colHidden')}</th>
							<th className="w-24 px-4 py-2 text-center font-medium">{t('catalog.categories.relations.colFeatured')}</th>
						</tr>
					</thead>
					<tbody>
						{isLoading ? (
							<tr>
								<td colSpan={4} className="px-4 py-8 text-center text-foreground-muted">
									…
								</td>
							</tr>
						) : rows.length === 0 ? (
							<tr>
								<td colSpan={4} className="px-4 py-8 text-center text-foreground-muted">
									{t('common.noResults')}
								</td>
							</tr>
						) : (
							rows.map(a => (
								<tr key={a.id} className="border-b border-border/60 hover:bg-muted/40">
									<td className="px-4 py-2 text-foreground">{a.attributeName}</td>
									<td className="px-4 py-2 text-foreground-muted">{a.attributeCode}</td>
									<td className="px-4 py-2 text-center">
										<ToggleCell
											checked={Boolean(a.hide)}
											label={t('catalog.categories.relations.colHidden')}
											onChange={v => update.mutate({ ...a, hide: v } as never, { onError: onErr })}
										/>
									</td>
									<td className="px-4 py-2 text-center">
										<ToggleCell
											checked={Boolean(a.isFeatured)}
											label={t('catalog.categories.relations.colFeatured')}
											onChange={v => update.mutate({ ...a, isFeatured: v } as never, { onError: onErr })}
										/>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</ScrollArea>
		</RelationShell>
	);
}

/* ------------------------------------------------------------------ dispatcher */

export function CategoryRelationsModal({
	category,
	relation,
	onClose
}: {
	category: CategoryToken | null;
	relation: RelationKind | null;
	onClose: () => void;
}) {
	if (!category || !relation) return null;
	switch (relation) {
		case 'products':
			return <ProductsManager category={category} onClose={onClose} />;
		case 'manufacturers':
			return <ManufacturersManager category={category} onClose={onClose} />;
		case 'news':
			return <NewsManager category={category} onClose={onClose} />;
		case 'attributes':
			return <AttributesManager category={category} onClose={onClose} />;
		default:
			return null;
	}
}
